import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { PaperPlaneRight, Image as ImageIcon, X } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSocket } from '../../contexts/SocketContext';
import { CapsuleComposeButton } from './SecureCapsule';
import {
  MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB, TYPING_TIMEOUT_MS,
  ALLOWED_UPLOAD_MIME_TYPES, UPLOAD_ACCEPT_ATTR,
} from '../../lib/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessageComposer = ({ chatId, inShell = false, capsuleAutoOpen = false, onCapsuleCancel }) => {
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const { sendMessage, startTyping, stopTyping } = useSocket();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const handleTyping = (value) => {
    setMessage(value);
    
    if (value.length > 0) {
      startTyping(chatId);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        stopTyping(chatId);
      }, TYPING_TIMEOUT_MS);
    } else {
      stopTyping(chatId);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file after an error
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File too large (max ${MAX_FILE_SIZE_MB} MB)`);
      return;
    }
    if (file.type && !ALLOWED_UPLOAD_MIME_TYPES.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP images and PDF files are allowed');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/media/upload?chat_id=${chatId}`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setUploadedMedia(data);
    } catch (error) {
      // Surface the backend's specific rejection reason when available.
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSend = () => {
    if (!message.trim() && !uploadedMedia) return;

    const sent = sendMessage(chatId, message.trim() || null, uploadedMedia?.id);
    if (!sent) return; // keep the draft so the user can retry after reconnect
    setMessage('');
    setUploadedMedia(null);
    stopTyping(chatId);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [message]);

  return (
    // In the member shell the navigation below already holds the safe area.
    <div className={`border-t border-border bg-card p-4${inShell ? '' : ' safe-bottom'}`} data-testid="message-composer">
      {uploadedMedia && (
        <div className="mb-3 inline-flex items-center gap-2 bg-muted rounded-lg px-3 py-2 text-sm">
          <ImageIcon className="w-4 h-4" />
          <span className="truncate max-w-[200px]">{uploadedMedia.original_filename}</span>
          <button
            onClick={() => setUploadedMedia(null)}
            className="ml-2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={UPLOAD_ACCEPT_ATTR}
          onChange={handleFileUpload}
          className="hidden"
        />
        
        <Button
          type="button"
          variant="ghost"
          size="touch-icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="shrink-0"
          aria-label="Attach an image or PDF"
          data-testid="upload-button"
        >
          <ImageIcon className="w-5 h-5" />
        </Button>

        <CapsuleComposeButton chatId={chatId} autoOpen={capsuleAutoOpen} onCancel={onCapsuleCancel} />

        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => handleTyping(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          aria-label="Your message"
          className="min-h-[44px] max-h-[120px] resize-none"
          rows={1}
          data-testid="message-input"
        />

        <Button
          onClick={handleSend}
          disabled={(!message.trim() && !uploadedMedia) || uploading}
          className="shrink-0 bg-primary hover:bg-primary/90"
          size="touch-icon"
          aria-label="Send"
          data-testid="send-button"
        >
          <PaperPlaneRight className="w-5 h-5" weight="fill" />
        </Button>
      </div>
    </div>
  );
};

export default MessageComposer;
